import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, ChevronDown, CheckCircle2, Plus, Loader2, Users,
  Briefcase, ArrowLeft, Layers,
} from "lucide-react";

/**
 * Persistent context bar for commercialisti. Two visual modes:
 *   1. CLIENT mode (default on /app, /app/invoices, /app/upload, /app/suppliers,
 *      /app/integrations): blue-accent bar with tab pills for each client.
 *   2. STUDIO mode (on /app/account/*): purple-accent bar signalling "Stai
 *      gestendo dati del tuo studio, non di un cliente" + quick link back to
 *      the active client's invoices.
 *
 * The scope switch is inferred from the URL path — no manual toggle. This
 * keeps the mental model tight: your route = your context.
 */
const STUDIO_PATH_PREFIX = "/app/account";

export default function CommercialistaModeBar() {
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [switching, setSwitching] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const scrollerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => companies.find((c) => c.is_active) || companies[0],
    [companies]
  );
  const isStudioMode = location.pathname.startsWith(STUDIO_PATH_PREFIX);

  const activate = async (id) => {
    if (id === active?.id) return;
    setSwitching(id);
    try {
      await api.post(`/companies/${id}/activate`);
      toast.success("Cliente attivato");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setSwitching(null);
    }
  };

  if (companies.length === 0) return null;

  /* ---------- STUDIO MODE ---------- */
  if (isStudioMode) {
    return (
      <div
        className="border-b border-[#E5DBFF] bg-[#F7F3FF] sticky top-14 lg:top-0 z-20"
        data-testid="mode-bar-studio"
      >
        <div className="px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#6D28D9] text-white text-[10px] font-bold uppercase tracking-[0.2em]">
            <Briefcase className="w-3.5 h-3.5" />
            Modalità studio
          </span>
          <span className="text-xs text-[#4C1D95] font-medium">
            Stai gestendo i dati del <span className="font-bold">tuo studio</span> — non di un cliente
          </span>
          <div className="ml-auto flex items-center gap-2">
            {active && (
              <Link
                to="/app/invoices"
                data-testid="mode-bar-back-to-client"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#4C1D95] text-xs font-semibold border border-[#E5DBFF] hover:border-[#6D28D9] transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Torna a <span className="font-bold truncate max-w-[120px]">{active.denominazione}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- CLIENT MODE ---------- */
  return (
    <div
      className="border-b border-[#DBEAFE] bg-gradient-to-r from-[#EFF6FF] via-white to-[#EFF6FF] sticky top-14 lg:top-0 z-20"
      data-testid="mode-bar-client"
    >
      <div className="px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-3 flex-wrap lg:flex-nowrap">
        {/* Left: scope pill + active client name */}
        <div className="flex items-center gap-2.5 min-w-0 shrink-0">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-[0.2em]">
            <Users className="w-3.5 h-3.5 text-[#60A5FA]" />
            Cliente attivo
          </span>
          {active && (
            <div className="min-w-0 hidden sm:block">
              <div className="text-[9px] uppercase tracking-widest text-[#737373] font-bold leading-none">Stai lavorando su</div>
              <div className="mt-0.5 text-sm font-display font-extrabold text-[#0A0A0A] truncate max-w-[240px]" title={active.denominazione}>
                {active.denominazione}
              </div>
            </div>
          )}
        </div>

        {/* Right: horizontally scrollable client tabs */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
          <div
            ref={scrollerRef}
            className={`flex items-center gap-1.5 min-w-0 ${expanded ? "flex-wrap" : "overflow-x-auto"} scrollbar-hide`}
            role="tablist"
            aria-label="Cliente attivo"
          >
            {companies.map((c) => {
              const isActive = c.id === active?.id;
              const isBusy = switching === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => activate(c.id)}
                  disabled={!!switching}
                  data-testid={`company-tab-${c.id}`}
                  aria-selected={isActive}
                  role="tab"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border shrink-0 ${
                    isActive
                      ? "bg-[#1D4ED8] text-white border-[#1D4ED8] shadow-sm shadow-blue-600/20"
                      : "bg-white text-[#4A4A4A] border-[#DBEAFE] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                  } ${switching && !isBusy ? "opacity-60" : ""}`}
                >
                  {isBusy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Building2 className={`w-3 h-3 ${isActive ? "text-white" : "text-[#737373]"}`} />
                  )}
                  <span className="truncate max-w-[160px]">{c.denominazione || "\u2014"}</span>
                  {isActive && <CheckCircle2 className="w-3 h-3 text-emerald-300" />}
                </button>
              );
            })}
            <Link
              to="/app/account/companies"
              data-testid="company-tab-add"
              title="Aggiungi cliente"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#1D4ED8] border border-dashed border-[#1D4ED8]/40 hover:bg-blue-50 transition-colors shrink-0"
            >
              <Plus className="w-3 h-3" />
              Aggiungi
            </Link>
          </div>
          {companies.length > 4 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="mode-bar-expand"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold text-[#4A4A4A] hover:bg-white shrink-0"
              title={expanded ? "Comprimi" : "Espandi"}
            >
              <Layers className="w-3 h-3" />
              {expanded ? "\u2013" : "+"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
